import type { Validations, Violation } from "@credit-acceptance/step6-bff-client";
import type { FieldPath } from "react-hook-form";

import { EMPLOYMENT_SLOTS_BY_INDEX } from "./Signers.putMapping";
import type { SignersInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// Signers.violations
//
// Translates the BFF's per-applicant Validations (requiredViolations /
// businessRuleViolations, each a dot-path `field` + human-readable message)
// onto this form's RHF field paths, so a backend violation can drive the same
// inline <FormMessage> the old Zod resolver used to. Pure — no React — the
// glue that calls form.setError with this module's output lives in
// useServerFieldErrors.ts.
//
// The `field` grammar is confirmed for employments.<slot>.<name> and
// addresses.<current|mailing>.<name> against a real BFF response (see the
// literal fixture in Signers.violations.test.ts); everything else below is a
// best-effort extrapolation from that same grammar and from the BFF
// write-model's own field names (Signers.putMapping.ts), NOT independently
// probe-confirmed:
//   - addresses.current.* (only addresses.mailing.* was actually observed)
//   - root customer fields (firstName/lastName/dateOfBirth/ssnOrItin/...)
//   - phones.<index>.<name> array grammar
//   - email (the BFF write model's emailAddresses[] has no 1:1 RHF counterpart
//     — the form stores one flat emailAddress string)
//   - bank.<name> vs a bare bankName/isCheckingAccount/isSavingsAccount/isPrepaidDebit
//   - employments.secondary/tertiary for income slots 1/2
//   - businessRuleViolations using the same field grammar as requiredViolations
//   - employmentType/incomeFrequencyType violations using the enum-id field
//     name (employmentTypeId/incomeFrequencyTypeId) vs the string-code name
//
// Because of this, any `field` that doesn't resolve — or a violation with no
// `field` at all — must never be silently dropped: it's returned in
// `unmapped` so the caller can still surface the message (e.g. in the
// existing section-level banner) instead of losing a real backend error.
// ---------------------------------------------------------------------------

export type ViolationSource = "required" | "businessRule";

export interface FieldErrorTarget {
  rhfPath: FieldPath<SignersInput>;
  message: string;
  source: ViolationSource;
  code: string;
}

export interface UnmappedViolation {
  violation: Violation;
  source: ViolationSource;
}

export interface TranslatedViolations {
  fieldErrors: FieldErrorTarget[];
  unmapped: UnmappedViolation[];
}

/** `employments.<slot>.<field>` — `IncomeInput` fields the BFF write-model names identically (Signers.putMapping.ts). */
const EMPLOYMENT_FIELD_MAP: Record<string, string> = {
  employmentType: "employmentType",
  employmentTypeId: "employmentType",
  employerName: "employerName",
  occupation: "occupation",
  streetNumber: "streetNumber",
  streetName: "streetName",
  apartmentNumber: "apartmentNumber",
  zip: "zip",
  city: "city",
  state: "state",
  employerPhone: "employerPhone",
  phoneExtension: "phoneExtension",
  differentWorkLocation: "differentWorkLocation",
  monthlyGrossPayAmount: "monthlyGrossPayAmount",
  hireDate: "hireDate",
  incomePerPayPeriodAmount: "incomePerPayPeriodAmount",
  incomeFrequencyType: "incomeFrequencyType",
  incomeFrequencyTypeId: "incomeFrequencyType",
  payType: "payType",
  notes: "notes",
  payStubDate: "payStubDate",
  payPeriodEndDate: "payPeriodEndDate",
  yearToDateIncomeAmount: "yearToDateIncomeAmount",
  isPaidOn1stAnd15: "isPaidOn1stAnd15",
};

/** `addresses.<current|mailing>.<field>` — the BFF's "current" key maps to this form's "physical" prefix; "mailing" matches directly. */
const ADDRESS_PREFIX_MAP: Record<string, "physical" | "mailing"> = {
  current: "physical",
  mailing: "mailing",
};

const ADDRESS_FIELD_SUFFIXES = new Set(["streetNumber", "streetName", "apartmentNumber", "zip", "city", "state"]);

/** `phones.<index>.<field>` — `PhoneInput` fields the BFF write-model names identically (Signers.putMapping.ts' mapPhoneRow). */
const PHONE_FIELD_MAP: Record<string, string> = {
  number: "number",
  extension: "extension",
  type: "type",
  phoneUsageTypeId: "type",
};

/** Bare/`bank.<field>` bank-account fields (Signers.putMapping.ts' mapBankAccountFields). */
const BANK_FIELD_MAP: Record<string, string> = {
  bankName: "bankName",
  isCheckingAccount: "isCheckingAccount",
  isSavingsAccount: "isSavingsAccount",
  isPrepaidDebit: "isPrepaidDebit",
};

/** Root customer fields — renamed per residenceMapping.ts' GET-side mirror of the same BFF names. */
const ROOT_CUSTOMER_FIELD_MAP: Record<string, string> = {
  firstName: "firstName",
  middleName: "middleInitial",
  lastName: "lastName",
  nameSuffix: "suffix",
  dateOfBirth: "dateOfBirth",
  ssnOrItin: "ssnOrItin",
  emailAddress: "emailAddress",
};

/**
 * Uppercases a string's first character, leaving the rest unchanged.
 * @param {string} value - The string to capitalize.
 * @returns {string} The capitalized string.
 */
function capitalize(value: string): string {
  return value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Resolves the employment slot index for a lowercase `primary`/`secondary`/`tertiary`
 * segment, reusing the same PRIMARY/SECONDARY/TERTIARY ordering as
 * Signers.putMapping.ts's EMPLOYMENT_SLOTS_BY_INDEX rather than a second,
 * possibly-drifting list.
 * @param {string} slot - The violation path's slot segment (any case).
 * @returns {number | undefined} The zero-based income index, or undefined when unrecognized.
 */
function resolveEmploymentSlotIndex(slot: string): number | undefined {
  const index = EMPLOYMENT_SLOTS_BY_INDEX.findIndex((candidate) => candidate.toLowerCase() === slot.toLowerCase());
  return index === -1 ? undefined : index;
}

/**
 * Resolves one violation's `field` path to an RHF field path for the given
 * applicant leg, or undefined when it doesn't match any known pattern.
 * @param {string} field - The violation's dot-path field (relative to the applicant leg).
 * @param {"driver" | "secondSigner"} personPath - Which leg this violation belongs to.
 * @param {number} incomeCount - The number of currently rendered income blocks, so an
 *   out-of-range employment slot resolves to undefined (unmapped) instead of targeting a
 *   field that isn't on screen.
 * @param {number} phoneCount - The number of currently rendered phone rows, so an
 *   out-of-range phone index resolves to undefined (unmapped) instead of targeting a
 *   field that isn't on screen — same rationale as `incomeCount` above.
 * @returns {FieldPath<SignersInput> | undefined} The resolved RHF path, if any.
 */
function resolveFieldPath(
  field: string,
  personPath: "driver" | "secondSigner",
  incomeCount: number,
  phoneCount: number
): FieldPath<SignersInput> | undefined {
  const segments = field.split(".");

  if (segments.length === 3 && segments[0].toLowerCase() === "employments") {
    const [, slot, name] = segments;
    const index = resolveEmploymentSlotIndex(slot);
    const formField = EMPLOYMENT_FIELD_MAP[name];
    if (index === undefined || index >= incomeCount || !formField) {
      return undefined;
    }
    return `${personPath}.incomes.${index}.${formField}` as FieldPath<SignersInput>;
  }

  if (segments.length === 3 && segments[0].toLowerCase() === "addresses") {
    const [, addressKind, name] = segments;
    const prefix = ADDRESS_PREFIX_MAP[addressKind.toLowerCase()];
    if (!prefix || !ADDRESS_FIELD_SUFFIXES.has(name)) {
      return undefined;
    }
    return `${personPath}.customerInfo.${prefix}${capitalize(name)}` as FieldPath<SignersInput>;
  }

  if (segments.length === 3 && segments[0].toLowerCase() === "phones") {
    const [, indexSegment, name] = segments;
    const index = Number(indexSegment);
    const formField = PHONE_FIELD_MAP[name];
    if (!Number.isInteger(index) || index < 0 || index >= phoneCount || !formField) {
      return undefined;
    }
    return `${personPath}.customerInfo.phones.${index}.${formField}` as FieldPath<SignersInput>;
  }

  if (segments.length === 2 && segments[0].toLowerCase() === "bank") {
    const formField = BANK_FIELD_MAP[segments[1]];
    return formField ? (`${personPath}.bankAccount.${formField}` as FieldPath<SignersInput>) : undefined;
  }

  if (segments.length === 1) {
    const bankField = BANK_FIELD_MAP[segments[0]];
    if (bankField) {
      return `${personPath}.bankAccount.${bankField}` as FieldPath<SignersInput>;
    }
    const rootField = ROOT_CUSTOMER_FIELD_MAP[segments[0]];
    if (rootField) {
      return `${personPath}.customerInfo.${rootField}` as FieldPath<SignersInput>;
    }
  }

  return undefined;
}

/**
 * Translates one applicant leg's Validations into RHF field errors, falling
 * back to `unmapped` for anything the mapping table doesn't (yet) cover, or
 * for a violation with no `field` at all (an object/cross-field-level finding).
 * @param {Validations | undefined} validations - The leg's requiredViolations/businessRuleViolations.
 * @param {"driver" | "secondSigner"} personPath - Which leg these violations belong to.
 * @param {{ incomeCount: number, phoneCount: number }} context - Context for resolving the violation.
 * @param {number} context.incomeCount - The currently rendered income-block count for this leg.
 * @param {number} context.phoneCount - The currently rendered phone-row count for this leg.
 * @returns {TranslatedViolations} The resolved field errors, plus anything left unmapped.
 */
export function translatePersonViolations(
  validations: Validations | undefined,
  personPath: "driver" | "secondSigner",
  context: { incomeCount: number; phoneCount: number }
): TranslatedViolations {
  const fieldErrors: FieldErrorTarget[] = [];
  const unmapped: UnmappedViolation[] = [];

  const tagged: Array<[Violation, ViolationSource]> = [
    ...(validations?.requiredViolations ?? []).map((violation): [Violation, ViolationSource] => [
      violation,
      "required",
    ]),
    ...(validations?.businessRuleViolations ?? []).map((violation): [Violation, ViolationSource] => [
      violation,
      "businessRule",
    ]),
  ];

  for (const [violation, source] of tagged) {
    const rhfPath = violation.field
      ? resolveFieldPath(violation.field, personPath, context.incomeCount, context.phoneCount)
      : undefined;
    if (rhfPath) {
      fieldErrors.push({ rhfPath, message: violation.message, source, code: violation.code });
    } else {
      unmapped.push({ violation, source });
    }
  }

  return { fieldErrors, unmapped };
}
